/**
 * 5-rule password validator — verbatim Flutter
 * `reset_password_new_password_bloc.dart:17-22`:
 *
 *   bool valid = (password.length >= 8) &&
 *       matches(password, '[0-9]') &&
 *       matches(password, '[a-z]') &&
 *       matches(password, '[@, _, !, \$, %]') &&
 *       matches(password, '[A-Z]');
 *
 * Used by Phase 3b (reset password new password), Phase 4 (create password
 * via email link), and Phase 5 (signup with access code). Shared helper so
 * the rules and display strings stay verbatim across all three flows.
 *
 * **Quirk preserved verbatim**: the special-char regex `[@, _, !, $, %]`
 * is a character class that includes comma `,` AND space ` ` as accepted
 * characters in addition to the 5 intended specials (@ _ ! $ %). The
 * displayed string says "(@, _, !, $ or %)" — a 5-char spec — but the
 * regex accepts 7 characters. Per CLAUDE.md magic-strings rule we copy
 * verbatim; this is intentional Flutter parity, not a React bug.
 */

import { matches } from './validators-matches';

/** A single rule's display string + pass/fail check. */
export interface PasswordRule {
  /** Display string shown under the password field (verbatim Flutter intl). */
  label: string;
  /** Returns true iff `password` satisfies this rule. */
  test: (password: string) => boolean;
}

/**
 * The 5 rules in the order they appear in Flutter
 * `reset_password_new_password/body_widgets.dart:69-128`.
 *
 * Display strings verbatim Flutter intl keys:
 * - atLeast8Characters
 * - OneNumber
 * - OneLowercase
 * - OneSpecialCharacterOr + " (@, _, !, $ or %)"
 * - OneUppercaseCharacter
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    label: 'At least 8 characters',
    test: (p) => p.length >= 8,
  },
  {
    label: '1 number',
    test: (p) => matches(p, '[0-9]'),
  },
  {
    label: '1 lowercase',
    test: (p) => matches(p, '[a-z]'),
  },
  {
    label: '1 special character or (@, _, !, $ or %)',
    test: (p) => matches(p, '[@, _, !, $, %]'),
  },
  {
    label: '1 uppercase character',
    test: (p) => matches(p, '[A-Z]'),
  },
];

/** Returns `true` iff all 5 rules pass on `password`. */
export function allRulesPass(password: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(password));
}

/**
 * Returns the per-rule pass/fail breakdown — for the visual rule list
 * under the password field. Order matches `PASSWORD_RULES`.
 */
export function evaluateRules(password: string): boolean[] {
  return PASSWORD_RULES.map((r) => r.test(password));
}

/** Mirrors Flutter `confirmPassword === password` check. */
export function passwordsMatch(password: string, confirm: string): boolean {
  return password === confirm && password.length > 0;
}
